import { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { ExportCSVButton, ExportPDFButton } from '../components/ExportButtons';
import { getHeatmapReport, listAllGolfCourses, listSettings } from '../api';
import ReportFilterBar from '../components/ReportFilterBar';
import { exportCSV, fmtReportDate, fmtCSVDate, fmtCSVFilename } from '../utils/reportExport';
import PrintBanner from '../components/PrintBanner/PrintBanner';
import styles from './HeatmapReportPage.module.css';

// ---------------------------------------------------------------------------
// Tier-based coloring by raw player count
// ---------------------------------------------------------------------------

const TIERS = [
  { label: 'Low',    color: '#E1F5EE', text: '#085041' },
  { label: 'Medium', color: '#5DCAA5', text: '#085041' },
  { label: 'High',   color: '#1D9E75', text: '#E1F5EE' },
  { label: 'Peak',   color: '#063D30', text: '#E1F5EE' },
];

// Week view: players per hour cell. Month view: players per day — much larger scale.
const DEFAULT_THRESHOLDS_WEEK  = [10, 25, 40];
const DEFAULT_THRESHOLDS_MONTH = [100, 250, 400];

const LS_KEY_WEEK  = 'heatmap_thresholds_week';
const LS_KEY_MONTH = 'heatmap_thresholds_month';

function loadThresholds(key, defaults) {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length === 3 && parsed.every(Number.isFinite)) {
        return parsed.map(Math.round);
      }
    }
  } catch {}
  return [...defaults];
}

function colorForPlayers(players, thresholds) {
  const idx = thresholds.findIndex(t => players <= t);
  const tier = idx === -1 ? TIERS[3] : TIERS[idx];
  return { bg: tier.color, tc: tier.text };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function mondayOfISO(iso) {
  const d = new Date(iso + 'T00:00:00');
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}

function fmtShort(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtDow(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short' });
}

function hLabel(h) {
  if (h === 12) return '12 pm';
  if (h < 12) return `${h} am`;
  return `${h - 12} pm`;
}

function pct(n) {
  return Math.round(n * 100) + '%';
}

// Build list of Monday-starting weeks from ISO dates
function buildWeeks(from, to) {
  const weeks = [];
  let cur = new Date(mondayOfISO(from) + 'T00:00:00');
  const toDate = new Date(to + 'T00:00:00');
  while (cur <= toDate) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(cur.toISOString().split('T')[0]);
      cur.setDate(cur.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

// Build list of months from ISO dates
function buildMonths(from, to) {
  const months = [];
  const fromD = new Date(from + 'T00:00:00');
  const toD   = new Date(to + 'T00:00:00');
  let y = fromD.getFullYear(), m = fromD.getMonth();
  while (y < toD.getFullYear() || (y === toD.getFullYear() && m <= toD.getMonth())) {
    months.push({ year: y, month: m });
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return months;
}

// Index cells by date+hour for fast lookup
function indexCells(cells) {
  const idx = {};
  for (const c of cells) {
    if (!idx[c.date]) idx[c.date] = {};
    idx[c.date][c.hour] = c;
  }
  return idx;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function HeatmapCell({ cell, date, hour, inRange, thresholds }) {
  if (!inRange) {
    return <div className={`${styles.cell} ${styles.cellOor}`} />;
  }
  if (!cell) {
    return <div className={`${styles.cell} ${styles.cellNoTt}`} />;
  }

  const { bg, tc } = colorForPlayers(cell.players, thresholds);
  const blocked = cell.has_block;

  const blockLines = (cell.blocks || []).map(
    b => `Block: ${b.name} (${b.course_name}) — ${b.blocked_slots} of ${cell.total_slots} slots`
  );

  const tooltip = [
    `${fmtDow(date)} ${fmtShort(date)} · ${hLabel(hour)}`,
    `${cell.players} player${cell.players !== 1 ? 's' : ''}`,
    ...blockLines,
  ].join('\n');

  return (
    <div
      className={`${styles.cell} ${blocked ? styles.cellBlocked : ''}`}
      style={{ background: bg, color: tc }}
      title={tooltip}
      aria-label={tooltip}
    >
      {cell.players > 0 ? cell.players : '0'}
    </div>
  );
}

function MonthCell({ data, date, inRange, thresholds }) {
  if (!date) return <div className={styles.monthDayEmpty} />;

  if (!inRange) {
    return (
      <div className={`${styles.monthDay} ${styles.monthDayOor}`}>
        <span className={styles.dayNum}>{parseInt(date.split('-')[2], 10)}</span>
      </div>
    );
  }

  if (!data || data.players_max === 0) {
    return (
      <div className={styles.monthDay} style={{ background: 'var(--cp-gray-50)' }}>
        <span className={styles.dayNum}>{parseInt(date.split('-')[2], 10)}</span>
      </div>
    );
  }

  const { bg, tc } = colorForPlayers(data.players, thresholds);

  const tooltip = [
    `${fmtShort(date)}`,
    `${data.players} players`,
    ...(data.blocks || []).map(b => `Block: ${b.name} (${b.course_name})`),
  ].join('\n');

  return (
    <div
      className={`${styles.monthDay} ${data.has_block ? styles.monthDayBlocked : ''}`}
      style={{ background: bg, color: tc }}
      title={tooltip}
      aria-label={tooltip}
    >
      <span className={styles.dayNum} style={{ color: tc }}>
        {parseInt(date.split('-')[2], 10)}
      </span>
      <span className={styles.dayUtil} style={{ color: tc }}>{data.players}</span>
      <span className={styles.dayPpl} style={{ color: tc }}>players</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Week view
// ---------------------------------------------------------------------------

function WeekView({ week, cellIdx, from, to, weekNum, totalWeeks, thresholds, onThresholdsChange, defaultThresholds }) {
  const inRange = (iso) => iso >= from && iso <= to;

  // Collect all unique hours that have cells in this week
  const hours = useMemo(() => {
    const set = new Set();
    week.forEach(iso => {
      if (cellIdx[iso]) Object.keys(cellIdx[iso]).forEach(h => set.add(Number(h)));
    });
    return [...set].sort((a, b) => a - b);
  }, [week, cellIdx]);

  // KPIs for this week's visible cells
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { totP, totM, peakH, quietH } = useMemo(() => {
    let tp = 0, tm = 0, peakU = -1, peakH = null, quietU = 999, quietH = null;
    hours.forEach(h => {
      let hp = 0, hm = 0, utilSum = 0, utilCount = 0;
      week.forEach(iso => {
        if (iso < from || iso > to) return;
        const c = cellIdx[iso]?.[h];
        if (c) {
          hp += c.players;
          hm += c.players_max;
          if (c.players_max > 0) {
            utilSum += c.avg_util ?? (c.players / c.players_max);
            utilCount++;
          }
        }
      });
      tp += hp; tm += hm;
      const u = utilCount > 0 ? utilSum / utilCount : 0;
      if (u > peakU && hm > 0) { peakU = u; peakH = h; }
      if (u < quietU && hm > 0 && hp > 0) { quietU = u; quietH = h; }
    });
    return { totP: tp, totM: tm, peakH, quietH: quietH === peakH ? null : quietH };
  }, [hours, week, cellIdx, from, to]);

  return (
    <>
      <div className={styles.kpiRow}>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Player utilization</div>
          <div className={styles.kpiVal}>{totM > 0 ? pct(totP / totM) : '—'}</div>
          <div className={styles.kpiSub}>{totP} players this week</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Busiest hour</div>
          <div className={styles.kpiVal}>{peakH !== null ? hLabel(peakH) : '—'}</div>
          <div className={styles.kpiSub}>{peakH !== null ? 'Most activity' : ''}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Slowest hour</div>
          <div className={styles.kpiVal}>{quietH !== null ? hLabel(quietH) : '—'}</div>
          <div className={styles.kpiSub}>{quietH !== null ? 'Least activity' : ''}</div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.tableScroll}>
          <table className={styles.hmTable}>
            <thead>
              <tr>
                <th className={styles.thHour}>Hour</th>
                {week.map(iso => (
                  <th
                    key={iso}
                    className={styles.thDay}
                    style={{ opacity: inRange(iso) ? 1 : 0.3 }}
                  >
                    <div className={styles.thDayName}>{fmtDow(iso)}</div>
                    <div className={styles.thDayDate}>{fmtShort(iso)}</div>
                  </th>
                ))}
                <th className={styles.thAvg}>Avg</th>
              </tr>
            </thead>
            <tbody>
              {hours.map(h => {
                let rP = 0, rM = 0;
                return (
                  <tr key={h}>
                    <td className={styles.rowLabel}>{hLabel(h)}</td>
                    {week.map(iso => {
                      const cell = inRange(iso) ? cellIdx[iso]?.[h] : null;
                      if (inRange(iso) && cell) { rP += cell.players; rM += cell.players_max; }
                      return (
                        <td key={iso} className={styles.cellTd}>
                          <HeatmapCell
                            cell={cell}
                            date={iso}
                            hour={h}
                            inRange={inRange(iso)}
                            thresholds={thresholds}
                          />
                        </td>
                      );
                    })}
                    <td className={styles.avgCell}>{rM > 0 ? pct(rP / rM) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className={styles.tfootLabel}>Avg util</td>
                {week.map(iso => {
                  if (!inRange(iso)) return <td key={iso} className={styles.tfootCell} style={{ opacity: 0.3 }} />;
                  let dp = 0, dm = 0;
                  hours.forEach(h => {
                    const c = cellIdx[iso]?.[h];
                    if (c) { dp += c.players; dm += c.players_max; }
                  });
                  return (
                    <td key={iso} className={styles.tfootCell}>
                      {dm > 0 ? pct(dp / dm) : '—'}
                    </td>
                  );
                })}
                <td className={styles.tfootAvg}>{totM > 0 ? pct(totP / totM) : '—'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <Legend thresholds={thresholds} defaultThresholds={defaultThresholds} onThresholdsChange={onThresholdsChange} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Month view
// ---------------------------------------------------------------------------

function MonthView({ year, month, cellIdx, from, to, thresholds, onThresholdsChange, defaultThresholds }) {
  const firstDay = new Date(year, month, 1);
  const inRange = (iso) => iso >= from && iso <= to;

  // All days in the month as ISO strings
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allDays = useMemo(() => {
    const days = [];
    let d = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    while (d <= last) {
      days.push(d.toISOString().split('T')[0]);
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, [year, month]);

  // KPIs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { totP, totM, peakDay, quietDay } = useMemo(() => {
    let tp = 0, tm = 0;
    let peakU = -1, peakDay = null, quietU = 999, quietDay = null;
    allDays.filter(iso => iso >= from && iso <= to).forEach(iso => {
      let dp = 0, dm = 0, utilSum = 0, utilCount = 0;
      if (cellIdx[iso]) {
        Object.values(cellIdx[iso]).forEach(c => {
          dp += c.players;
          dm += c.players_max;
          if (c.players_max > 0) {
            utilSum += c.avg_util ?? (c.players / c.players_max);
            utilCount++;
          }
        });
      }
      tp += dp; tm += dm;
      const u = utilCount > 0 ? utilSum / utilCount : 0;
      if (u > peakU && dm > 0) { peakU = u; peakDay = iso; }
      if (u < quietU && dm > 0 && dp > 0) { quietU = u; quietDay = iso; }
    });
    return { totP: tp, totM: tm, peakDay, quietDay: quietDay === peakDay ? null : quietDay };
  }, [allDays, cellIdx, from, to]);

  // Padding before first day (Mon=0, ..., Sun=6)
  const firstDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

  // Per-day aggregated data
  const dayData = useMemo(() => {
    const map = {};
    allDays.forEach(iso => {
      if (!cellIdx[iso]) { map[iso] = null; return; }
      let dp = 0, dm = 0, hasBlock = false;
      const blocksSeen = new Map();
      Object.values(cellIdx[iso]).forEach(c => {
        dp += c.players; dm += c.players_max;
        if (c.has_block) {
          hasBlock = true;
          (c.blocks || []).forEach(b => {
            const key = `${b.name}||${b.course_name}`;
            if (!blocksSeen.has(key)) blocksSeen.set(key, b);
          });
        }
      });
      map[iso] = { players: dp, players_max: dm, has_block: hasBlock, blocks: [...blocksSeen.values()] };
    });
    return map;
  }, [allDays, cellIdx]);

  return (
    <>
      <div className={styles.kpiRow}>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Player utilization</div>
          <div className={styles.kpiVal}>{totM > 0 ? pct(totP / totM) : '—'}</div>
          <div className={styles.kpiSub}>{totP} players this month</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Busiest day</div>
          <div className={styles.kpiVal}>{peakDay ? fmtShort(peakDay) : '—'}</div>
          <div className={styles.kpiSub}>{peakDay && dayData[peakDay] ? `${pct(dayData[peakDay].players / dayData[peakDay].players_max)} utilization` : ''}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Slowest day</div>
          <div className={styles.kpiVal}>{quietDay ? fmtShort(quietDay) : '—'}</div>
          <div className={styles.kpiSub}>{quietDay && dayData[quietDay] ? `${pct(dayData[quietDay].players / dayData[quietDay].players_max)} utilization` : ''}</div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.monthDowHeader}>
          {DOW.map(d => <div key={d} className={styles.monthDowLabel}>{d}</div>)}
        </div>
        <div className={styles.monthGrid}>
          {Array.from({ length: firstDow }).map((_, i) => (
            <div key={`pad-${i}`} className={styles.monthDayEmpty} />
          ))}
          {allDays.map(iso => (
            <MonthCell
              key={iso}
              date={iso}
              data={dayData[iso]}
              inRange={inRange(iso)}
              thresholds={thresholds}
            />
          ))}
        </div>
        <Legend thresholds={thresholds} defaultThresholds={defaultThresholds} onThresholdsChange={onThresholdsChange} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend({ thresholds, defaultThresholds, onThresholdsChange }) {
  const [open, setOpen] = useState(false);
  // Local draft state for inputs — prevents mid-type cascades
  const [draft, setDraft] = useState([...thresholds]);

  // Sync draft when thresholds change externally (e.g. reset)
  useEffect(() => { setDraft([...thresholds]); }, [thresholds]);

  // Build tier range labels from current thresholds
  const tierRanges = [
    `0–${thresholds[0]}`,
    `${thresholds[0] + 1}–${thresholds[1]}`,
    `${thresholds[1] + 1}–${thresholds[2]}`,
    `${thresholds[2] + 1}+`,
  ];

  function handleInputChange(idx, raw) {
    const updated = [...draft];
    updated[idx] = raw === '' ? '' : Number(raw);
    setDraft(updated);
  }

  function handleBlur(idx) {
    const newThresholds = [...thresholds];
    let val = Math.round(Number(draft[idx]));
    if (!Number.isFinite(val) || val < 1) val = 1;
    // Clamp: must be > previous threshold
    const min = idx === 0 ? 1 : newThresholds[idx - 1] + 1;
    if (val < min) val = min;
    newThresholds[idx] = val;
    // Cascade upward: if this value pushed into next tiers, bump them
    for (let i = idx + 1; i < newThresholds.length; i++) {
      if (newThresholds[i] <= newThresholds[i - 1]) {
        newThresholds[i] = newThresholds[i - 1] + 1;
      }
    }
    setDraft([...newThresholds]);
    onThresholdsChange(newThresholds);
  }

  function handleReset() {
    setDraft([...defaultThresholds]);
    onThresholdsChange([...defaultThresholds]);
  }

  return (
    <div className={styles.legendWrapper}>
      <div className={styles.legendTiers}>
        {TIERS.map((tier, i) => (
          <div key={tier.label} className={styles.legendTier}>
            <div
              className={styles.legendTierSwatch}
              style={{ background: tier.color, border: `1px solid ${tier.color === '#E1F5EE' ? '#C5EBD9' : tier.color}` }}
            />
            <span>{tier.label}</span>
            <span style={{ color: 'var(--cp-gray-400)' }}>{tierRanges[i]}</span>
          </div>
        ))}
        <div className={styles.legendBlockItem}>
          <div className={styles.legendBlockSwatch} />
          Block active
        </div>
        <button
          className={styles.legendGearBtn}
          onClick={() => setOpen(o => !o)}
          aria-label="Configure thresholds"
          aria-expanded={open}
          title="Configure thresholds"
        >
          <Settings size={14} strokeWidth={1.5} />
        </button>
      </div>

      {open && (
        <div className={styles.thresholdPanel}>
          <div className={styles.thresholdPanelHeader}>
            <span>Thresholds</span>
            <button className={styles.thresholdResetBtn} onClick={handleReset}>
              Reset to default
            </button>
          </div>
          {[0, 1, 2].map(idx => {
            const prevMax = idx === 0 ? 0 : thresholds[idx - 1];
            return (
              <div key={idx} className={styles.thresholdRow}>
                <span className={styles.thresholdLabel}>{TIERS[idx].label}</span>
                <span>{idx === 0 ? '0' : `${prevMax + 1}`} to</span>
                <input
                  className={styles.thresholdInput}
                  type="number"
                  min={idx === 0 ? 1 : thresholds[idx - 1] + 1}
                  value={draft[idx]}
                  onChange={e => handleInputChange(idx, e.target.value)}
                  onBlur={() => handleBlur(idx)}
                />
                <span>players</span>
              </div>
            );
          })}
          <div className={styles.thresholdPeakRow}>
            <span className={styles.thresholdLabel}>Peak</span>
            <span>{thresholds[2] + 1}+ players</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function HeatmapReportPage() {
  const today = todayISO();
  const [dateFrom, setDateFrom]   = useState(addDaysISO(today, -27));
  const [dateTo, setDateTo]       = useState(today);
  const [courseId, setCourseId]   = useState('');
  const [courses, setCourses]     = useState([]);
  const [view, setView]           = useState('week'); // 'week' | 'month'
  const [weekThresholds, setWeekThresholds]   = useState(() => loadThresholds(LS_KEY_WEEK, DEFAULT_THRESHOLDS_WEEK));
  const [monthThresholds, setMonthThresholds] = useState(() => loadThresholds(LS_KEY_MONTH, DEFAULT_THRESHOLDS_MONTH));

  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [cells, setCells]         = useState([]);
  const [companyInfo, setCompanyInfo] = useState({ name: '', address: '', website: '', logo: '' });

  const [weekIdx, setWeekIdx]     = useState(0);
  const [monthIdx, setMonthIdx]   = useState(0);
  const [from, setFrom]           = useState('');
  const [to, setTo]               = useState('');

  useEffect(() => {
    listAllGolfCourses()
      .then(r => setCourses(r.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    listSettings()
      .then(data => {
        const map = {};
        (data.settings || data.items || []).forEach(s => { map[s.key] = s.value; });
        setCompanyInfo({
          name:    map.company_name    || '',
          address: map.company_address || '',
          website: map.company_website || '',
          logo:    map.company_logo    || '',
        });
      })
      .catch(() => {});
  }, []);

  const handleApply = () => {
    if (!dateFrom || !dateTo || dateFrom > dateTo) return;
    setLoading(true);
    setError(null);
    setCells([]);
    setWeekIdx(0);
    setMonthIdx(0);
    setFrom(dateFrom);
    setTo(dateTo);
    getHeatmapReport({ dateFrom, dateTo, courseId: courseId || undefined })
      .then(d => { setCells(d.cells || []); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  const handleWeekThresholdsChange = (newThresholds) => {
    setWeekThresholds(newThresholds);
    localStorage.setItem(LS_KEY_WEEK, JSON.stringify(newThresholds));
  };

  const handleMonthThresholdsChange = (newThresholds) => {
    setMonthThresholds(newThresholds);
    localStorage.setItem(LS_KEY_MONTH, JSON.stringify(newThresholds));
  };

  const cellIdx  = useMemo(() => indexCells(cells), [cells]);
  const weeks    = useMemo(() => from && to ? buildWeeks(from, to) : [], [from, to]);
  const months   = useMemo(() => from && to ? buildMonths(from, to) : [], [from, to]);

  const hasCells = cells.length > 0;
  const currentWeek  = weeks[weekIdx]  || [];
  const currentMonth = months[monthIdx] || null;

  const weekLabel = currentWeek.length > 0
    ? `${fmtShort(currentWeek[0])} – ${fmtShort(currentWeek[6])}, ${currentWeek[6].split('-')[0]}`
    : '—';
  const monthLabel = currentMonth
    ? `${MONTHS[currentMonth.month]} ${currentMonth.year}`
    : '—';

  const handleExport = () => {
    if (!cells.length) return;
    exportCSV(
      ['Date', 'Hour', 'Players', 'Players max', 'Utilization %', 'Has block', 'Block names'],
      cells.map(c => [
        fmtCSVDate(c.date), c.hour, c.players, c.players_max,
        c.players_max > 0 ? Math.round((c.players / c.players_max) * 100) + '%' : '—',
        c.has_block ? 'Yes' : 'No',
        (c.blocks || []).map(b => b.name).join(' | '),
      ]),
      fmtCSVFilename('heatmap', from, to),
    );
  };

  const handleExportPDF = () => window.print();

  return (
    <div className={styles.page}>
      {/* Toolbar — hidden when printing */}
      <div className="no-print">
      <ReportFilterBar
        title="Heatmap"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        courseId={courseId}
        courses={courses}
        onCourseChange={setCourseId}
        onApply={handleApply}
      >
        <span className={styles.sep} />
        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewBtn} ${view === 'week' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('week')}
          >
            Week
          </button>
          <button
            className={`${styles.viewBtn} ${view === 'month' ? styles.viewBtnActive : ''}`}
            onClick={() => setView('month')}
          >
            Month
          </button>
        </div>
        {hasCells && (
          <>
            <ExportCSVButton onClick={handleExport} />
            <ExportPDFButton onClick={handleExportPDF} />
          </>
        )}
      </ReportFilterBar>
      </div>{/* end noPrint */}

      {/* Body */}
      <div className={styles.body}>
        {loading && <div className={styles.emptyState}>Loading...</div>}
        {error && <div className={styles.errorState}>Failed to load data. Please try again.</div>}
        {!loading && !error && !hasCells && (
          <div className={styles.emptyState}>
            Set a date range and click Apply to load the heatmap.
          </div>
        )}

        {!loading && hasCells && (
          <>
            {/* ── Screen layout: paginated single-period view ── */}
            <div className={styles.screenLayout}>
              <div className={styles.navRow}>
                <div>
                  <div className={styles.navPeriod}>
                    {view === 'week' ? weekLabel : monthLabel}
                  </div>
                  <div className={styles.navSub}>
                    {view === 'week'
                      ? `Week ${weekIdx + 1} of ${weeks.length}`
                      : `Month ${monthIdx + 1} of ${months.length}`}
                  </div>
                </div>
                <div className={styles.navBtns}>
                  <button
                    className={styles.navBtn}
                    onClick={() => view === 'week' ? setWeekIdx(i => i - 1) : setMonthIdx(i => i - 1)}
                    disabled={view === 'week' ? weekIdx === 0 : monthIdx === 0}
                    aria-label="Previous period"
                  >
                    <ChevronLeft size={16} strokeWidth={1.5} />
                  </button>
                  <button
                    className={styles.navBtn}
                    onClick={() => view === 'week' ? setWeekIdx(i => i + 1) : setMonthIdx(i => i + 1)}
                    disabled={view === 'week' ? weekIdx >= weeks.length - 1 : monthIdx >= months.length - 1}
                    aria-label="Next period"
                  >
                    <ChevronRight size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              {view === 'week' && currentWeek.length > 0 && (
                <WeekView
                  week={currentWeek}
                  cellIdx={cellIdx}
                  from={from}
                  to={to}
                  weekNum={weekIdx + 1}
                  totalWeeks={weeks.length}
                  thresholds={weekThresholds}
                  defaultThresholds={DEFAULT_THRESHOLDS_WEEK}
                  onThresholdsChange={handleWeekThresholdsChange}
                />
              )}

              {view === 'month' && currentMonth && (
                <MonthView
                  year={currentMonth.year}
                  month={currentMonth.month}
                  cellIdx={cellIdx}
                  from={from}
                  to={to}
                  thresholds={monthThresholds}
                  defaultThresholds={DEFAULT_THRESHOLDS_MONTH}
                  onThresholdsChange={handleMonthThresholdsChange}
                />
              )}
            </div>

            {/* ── Print layout: all periods stacked, one per page ── */}
            <div className={styles.printLayout}>
              <PrintBanner
                companyInfo={companyInfo}
                reportTitle="Heatmap Report"
                dateRange={from && to ? `${fmtReportDate(from)} – ${fmtReportDate(to)}` : undefined}
                course={courseId ? (courses.find(c => c.id === Number(courseId))?.name || 'All courses') : 'All courses'}
                generatedOn={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              />

              {view === 'week' && weeks.map((week, i) => (
                <div key={i} className={styles.printPage}>
                  <div className={styles.printPageNav}>
                    <div className={styles.navPeriod}>
                      {`${fmtShort(week[0])} – ${fmtShort(week[6])}, ${week[6].split('-')[0]}`}
                    </div>
                    <div className={styles.navSub}>Week {i + 1} of {weeks.length}</div>
                  </div>
                  <WeekView
                    week={week}
                    cellIdx={cellIdx}
                    from={from}
                    to={to}
                    weekNum={i + 1}
                    totalWeeks={weeks.length}
                    thresholds={weekThresholds}
                    defaultThresholds={DEFAULT_THRESHOLDS_WEEK}
                    onThresholdsChange={handleWeekThresholdsChange}
                  />
                </div>
              ))}
              {view === 'month' && months.map((m, i) => (
                <div key={`${m.year}-${m.month}`} className={styles.printPage}>
                  <div className={styles.printPageNav}>
                    <div className={styles.navPeriod}>{MONTHS[m.month]} {m.year}</div>
                    <div className={styles.navSub}>Month {i + 1} of {months.length}</div>
                  </div>
                  <MonthView
                    year={m.year}
                    month={m.month}
                    cellIdx={cellIdx}
                    from={from}
                    to={to}
                    thresholds={monthThresholds}
                    defaultThresholds={DEFAULT_THRESHOLDS_MONTH}
                    onThresholdsChange={handleMonthThresholdsChange}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
